import { catchError, debounceTime, distinctUntilChanged, map, skip, switchMap, take, tap } from "rxjs/operators";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from "@angular/core";

import { FiatService } from "@main-services/currency/fiat.service";

import {
	Cryptocoin,
	Fiat,
	FiatWallet,
	ISwapFee,
	ITaxSwapAPIModel,
	IVSNFeeTokenCollection,
	OfflineError,
	SwapOfferModel,
	Wallet
} from "@models";

import { convertArrayToDictionary, Helper, parseObject } from "@helpers/functions";
import { SwapSuccessComponent } from "../../../main/components/step-summary/success-screens/swap-success/swap-success.component";
import { TrackingModal } from "../../../main/components/tracking-modal/tracking.modal";
import { Currency } from "@models/interfaces/currency";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from "rxjs";
import { SocketPool, SocketPoolService } from "@main-services/socket-pool.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Index } from "@models/asset/index/index.model";
import { SwapConfirmTrade } from "@models/swap-confirm-trade";
import { IndexWallet } from "@models/asset/index/index_wallet";
import {
	ALL_FILTERS_AVAILABLE,
	APPROPRIATENESS_REQUIRED_ERROR_CODE,
	BEST_SYMBOL,
	CRYPTO_TYPE,
	INDEX_TYPE,
	INFINITE_SEARCH_DELAY,
	MAX_CHARACTERS_FOR_DISPLAY,
	PRECISION_FOR_TRANSACTION,
	TAXES_NOT_ELIGIBLE_COOKIE_KEY,
	TOKEN_BEST_ID,
	TRADABLES_TYPE,
	VERIFICATION_MODAL_ROUTE
} from "@helpers/constants";
import { StockTestService } from "@main-services/stock-test.service";
import { Warning } from "@models/trade-offer.model";
import { OfflineModalComponent } from "../../../main/components/offline-modal/offline-modal.component";
import { Router } from "@angular/router";
import {
	changeTokenUnit,
	containsString,
	equal,
	setPrecisionDecimals,
	stripFunnyInput,
	truncate,
	truncateFixed
} from "@helpers/utils";
import { convertTradableToFiat, toCurrencyByFiatId } from "@helpers/tradable-convert";
import { AssetsDropdownScrollService } from "@main-services/assets-dropdown-scroll.service";
import { AssetInfoService } from "@main-services/split-data/asset-info.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AccountService } from "../../../account/services/account.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { Best2TermsModalComponent } from "../best-2-terms-modal/components/best-2-terms-modal.component";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { SwapTogglesComponent } from "@modules/trade/components/swap-toggles/swap-toggles.component";
import { Dictionary } from "@helpers/types";
import { Feature } from "@models/feature-list";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SwapSuccessV1Component } from "@modules/main/components/step-summary/success-screens/swap-success/swap-success-redesign/swap-success-redesign.component";
import { SwapAmountDefinedFor, SwapOfferAcceptSuccessResponse, SwapSuccessResponse } from "@gql/offer";
import { TradeSwapService } from "@modules/trade/services/trade-swap.service";
import { TradeSwapPendingComponent } from "@modules/main/components/step-summary/success-screens/trade-swap-pending/trade-swap-pending.component";
import { StakedTradeFacade } from "@modules/staking/domain/+state/staked-trade.facade";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { VerificationModalFacade } from "@modules/verification/modal/state/verification-modal.facade";
import { TaxesFacade } from "@modules/taxes/domain/+state/taxes.facade";
import { TaxesQueryParamName, TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import {
	ITaxSwap,
	ITaxWithheld,
	TaxesRouteNames,
	TaxesUserStatusModel,
	ToastMessages
} from "@modules/taxes/domain/entities/taxes.model";
import { TaxesStateService } from "@modules/taxes/domain/+state/taxes.state.service";
import { ToastrService } from "ngx-toastr";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import {
	OrderTypeTrackingProperty,
	TradeTrackingName,
	TradeTypeTrackingProperty
} from "@modules/checkout/entities/trade.rudderstack.tracking.model";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import {
	AgreementBundle,
	tradablesWithoutAppropriateness,
	UserJourney
} from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { LegalAcceptanceComponent } from "@modules/main/components/legal-acceptance-modal/legal-acceptance.component";
import { SwapOfferCreateMutation } from "../../../../api/graphql/offer/generated/swap.offer.create.service.graphql-gen";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import {
	visionBenefitToggleTexts,
	VSNOptinToggleViewModel,
	WEB3_FEE_BENEFFIT_FLOW,
	Web3VSNFeeBenefitFF
} from "@modules/web3/interface/web3.benefit.ui.interface";
import { IVisionSummarySwapTotalComponentInput } from "@modules/web3/ui/vision-summary-swap-total/vision-summary-swap-total.component";
import { StockWallet } from "@models/derivative/wallets/stock_wallet";
import { MetalWallet } from "@models/asset/metal/metal_wallet";
import { EtfWallet } from "@models/derivative/wallets/etf_wallet";
import { EtcWallet } from "@models/derivative/wallets/etc_wallet";
import { SecurityTokenWallet } from "@models/asset/cryptocoins/wallets/security_token_wallet";
import { LeveragedTokenWallet } from "@models/asset/cryptocoins/wallets/leveraged_token_wallet";
import { DigiSunsetService } from "@modules/real-stocks/digi-sunset/digi-sunset.service";
import { StatsigClientService } from "@core-services/statsig/statsig.service";
import { ExternalLauchOption, SwapDefaultPricesModel, TradeSwapFacade } from "./trade-swap.facade";

export interface ICombinedViewObservables {
	isSwapReady: boolean;
	showAmountSection: boolean;
	isLoading: boolean;
	assetList: BpSelectOption[];
	taxStatusLoaded: boolean;
}

@UntilDestroy()
@Component({
	templateUrl: "./trade-swap.component.html",
	styleUrls: ["./trade-swap.component.scss"],
	providers: [AssetsDropdownScrollService, SwapTogglesComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TradeSwapComponent extends TrackingModal implements OnInit, OnDestroy {
	@ViewChild(SwapTogglesComponent) swapToggles: SwapTogglesComponent;
	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;

	@Input()
	public fromTWFlow: boolean = false;
	// Used to pass the previous asset ids from tax withholding flow
	@Input()
	public previousLeftAssetId: string = "";
	@Input()
	public previousRightAssetId: string = "";

	private readonly taxesFacade = inject(TaxesFacade);
	private readonly taxesState = inject(TaxesStateService);
	private readonly toastr = inject(ToastrService);
	private tCService: TermsAndConditionsService = inject(TermsAndConditionsService);
	private digiSunsetService: DigiSunsetService = inject(DigiSunsetService);
	private tradeSwapFacade = inject(TradeSwapFacade);

	public warnings: Warning[] = [];

	private termsConditionsFf = null;
	public termsConditionsFeatureFlag$ = this.launchDarklyService
		.isActive$(LD_FLAG.TERMS_AND_CONDITIONS)
		.pipe(tap((active) => (this.termsConditionsFf = active)));

	public bundles$: Observable<AgreementBundle[]> = EMPTY;
	private bundles: AgreementBundle[] = [];
	public tradable: Tradable = null;

	public featureList = Feature;

	private tradablesHash: Dictionary<Tradable> = {};

	protected label: string = "swap";
	protected category: string = "trade";
	public focusChanged: boolean = false;
	public inputChanged: boolean = false;
	protected amountChanged: boolean = false;
	public estimateLoading: boolean = false;

	public emptyStatePlaceHolder: string = "choose_asset_dropdown_placeholder";
	public leftSelection: SwapSelect = new SwapSelect("left", [TradableWallet]);
	public rightSelection: SwapSelect = new SwapSelect("right", [TradableWallet]); // For mixed list, just add [SwapSelect.TYPE_FIAT, SwapSelect.TYPE_CRYPTO]
	public tempRightSelection: TradableWallet = null; // Stores selection in case the flow is interrupted after tradable selection

	private defaultPricesAPIdata: SwapDefaultPricesModel;
	private swapOfferAPIdata: SwapOfferModel = new SwapOfferModel();

	private totalReceived: number = 0;
	private totalReceivedEquivalent: number = 0;
	private swapOfferId: string = "";
	public hasInputError: boolean = false;
	public amountTooLow: boolean = false;
	private amountExceedsBalance: boolean = false;

	public conversionRateOneUnit: string = "";

	public showConversionRate: boolean = false;

	public sliderPercent: string = "0% ";
	public sliderValue: number = 0;
	public totalAssetAmount: string = "";

	public appTestTradingWarning = false;

	// Time used to deactivate the Agree Terms button switch after inactivity (currently 30 sec)
	private oldTimer: number;
	private time: number = 0;

	private _externalLaunchOptions: ExternalLauchOption = null;
	private pool: SocketPool = null;

	private sliderValueChanged: Subject<void> = new Subject();
	private success: boolean = false;
	private apiPending: boolean = false;

	private pageSize: number = 300;

	private searchInput$: BehaviorSubject<string> = new BehaviorSubject<string>("");
	private isSwapReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private showAmountSection$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private taxStatusLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public combinedViewObservables$ = combineLatest(
		// Combines all observables necessary in the view into one
		{
			isSwapReady: this.isSwapReady$,
			showAmountSection: this.showAmountSection$,
			isLoading: this.assetsDropdownScrollService.getLoadingObservable(),
			assetList: this.assetsDropdownScrollService.getListObservable(),
			taxStatusLoaded: this.taxStatusLoaded$
		}
	).pipe(
		map(({ isSwapReady, showAmountSection, isLoading, assetList, taxStatusLoaded }) => {
			assetList = assetList.filter((asset) => {
				const assetValue = asset.value;

				return (
					!(assetValue instanceof StockWallet) &&
					!(assetValue instanceof MetalWallet) &&
					!(assetValue instanceof EtfWallet) &&
					!(assetValue instanceof SecurityTokenWallet) &&
					!(assetValue instanceof LeveragedTokenWallet) &&
					!(assetValue instanceof EtcWallet)
				);
			});

			return {
				isSwapReady,
				showAmountSection,
				isLoading,
				assetList,
				taxStatusLoaded
			};
		})
	);
	private showDefaultError: boolean = false;

	private selectedLeftAssetId$: BehaviorSubject<string> = new BehaviorSubject<string>("");

	public isAssetStaked$: Observable<boolean> = this.stakedTradeFacade.isStakedAsset$();

	// +++++ tax withheld +++++
	private taxWithheldBS$: BehaviorSubject<ITaxSwap> = new BehaviorSubject<ITaxSwap>(null);
	public taxWithheld$: Observable<ITaxSwap | null> = this.taxesFacade.isTaxesForTradeActive$.pipe(
		switchMap((active) => {
			if (active) {
				return this.taxWithheldBS$.asObservable();
			}
			return of(null);
		})
	);
	public twSummaryVariationFF$ = this.taxesFacade.isTaxesTradeSummaryVariationActive$;
	private taxStatus: string = "";
	private taxableSwapStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public hideTargetSymbol$: Observable<boolean> = combineLatest({
		ff: this.taxesFacade.isTaxesForTradeActive$,
		taxableSwap: this.taxableSwapStatus$.asObservable(),
		swapToGraphqlFf: this.launchDarklyService.isActive$(LD_FLAG.SWAP_SERVICE_GRAPHQL)
	}).pipe(
		map(({ ff, taxableSwap, swapToGraphqlFf }) => {
			if (swapToGraphqlFf) {
				return ff && this.taxStatus === "ELIGIBLE";
			}
			return ff && this.taxStatus === "ELIGIBLE" && taxableSwap;
		})
	);

	//fee transparency
	swapFee: ISwapFee = null;
	isFeeTransparent = false;
	feeTransparencyCryptoOnlyEnabled$ = this.launchDarklyService.isActive$(LD_FLAG.FEE_TRANSPARENCY_CRYPTO_ONLY);
	userFiat: Fiat = this.fiatService.getItemById(this.userService.user.getFiatId());

	// Used when the Swap is launched from other views (eg: Wallet details)
	public userJourneys: UserJourney[];

	private swapGraphqlFlag = false;
	public acceptOfferPending = false;

	//VSN benefit
	visionBenefitFF$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => d[WEB3_FEE_BENEFFIT_FLOW.swap]));

	vsnFeeBenefit?: VSNOptinToggleViewModel;
	vsnBenefitTotalVM: IVisionSummarySwapTotalComponentInput;
	private _visionBenefitToggleTexts = visionBenefitToggleTexts;
	vsnFeeCollection: IVSNFeeTokenCollection;

	private _bonusFlag: boolean = null;

	public constructor(
		protected walletService: TradableWalletListService,
		protected fiatService: FiatService,
		protected tradableListService: TradableListService,
		protected fiatwalletService: FiatWalletService,
		protected apicallService: ApicallService,
		protected userService: UserService,
		protected infoService: InfoService,
		private verificationModalFacade: VerificationModalFacade,
		protected accountService: AccountService,
		protected masterdataService: MasterdataService,
		protected socketPoolService: SocketPoolService,
		protected stockTestService: StockTestService,
		protected assetInfoService: AssetInfoService,
		protected router: Router,
		private assetsDropdownScrollService: AssetsDropdownScrollService,
		protected stockTestHelperService: StockTestHelperService,
		protected launchDarklyService: LaunchDarklyService,
		private tradeSwapService: TradeSwapService,
		protected stakedTradeFacade: StakedTradeFacade,
		protected stakingFacade: StakingFacade,
		private cdRef: ChangeDetectorRef,
		protected statsigService: StatsigClientService
	) {
		super();

		this.tradablesHash = convertArrayToDictionary(this.tradableListService.list, "id");
		this.initDropdowns();
		this.pool = socketPoolService.createPool(walletService.getList());
		this.pool
			.getPool()
			.pipe(untilDestroyed(this), skip(1)) // Should only fire for socket events, not on initial sub
			.subscribe(() => {
				this._externalLaunchOptions = null;
				this.lastDropdownApiCallOptions = null;
				this.resetLeftSideAfterTrade();
			});

		this.searchInput$
			.pipe(debounceTime(INFINITE_SEARCH_DELAY), distinctUntilChanged(), untilDestroyed(this))
			.subscribe((value) => this.assetsDropdownScrollService.searchListUpdate(value, this.pageSize));
	}

	public ngOnInit(): void {
		this.tCService.checkTCaccepted((bundles: AgreementBundle[]) => {
			this.modalService.hideAll();
			this.modalService.showModal(LegalAcceptanceComponent, { bundles });
		});
		this.trackInitiated();
		this.initTracking();
		this.initAssetList();
		if (!this.rightSelection.currentWallet && !this.leftSelection.currentWallet) {
			// In case the swap is completely empty, the right side dropdown is pre-selected
			this.leftSelection.currentWallet = this.leftSelection.bpSelectOptions[0].value;
		}

		//get tax status implicitly due to the fact that the swap is initialized
		this.taxesState.taxStatus$.pipe(take(1)).subscribe({
			next: (status) => {
				this.taxStatus = status;
			}
		});

		//swap gql flag
		this.launchDarklyService
			.isActive$(LD_FLAG.SWAP_SERVICE_GRAPHQL)
			.pipe(tap((active) => (this.swapGraphqlFlag = active)))
			.subscribe();
	}

	// ------------ Deals with the right side dropdown infinite scroll

	private initAssetList(): void {
		let currentTradable: Tradable = null;
		if (this.rightSelection.currentWallet) {
			currentTradable = this.getTradable(this.rightSelection.currentWallet);
		}
		this.assetsDropdownScrollService.setCurrentTradable(currentTradable);
		this.assetsDropdownScrollService.initList(this.pageSize, ALL_FILTERS_AVAILABLE); // Fetches first X options on startup
	}

	// The dropdown always has to keep the currently selected tradable in mind in case a search is started and aborted
	private setCurrentTradable(): void {
		const currentTradable: Tradable = this.tradableListService.getItemById(
			this.rightSelection.currentWallet.getCurrencyId()
		);
		this.assetsDropdownScrollService.setCurrentTradable(currentTradable);
	}

	public fetchListUpdate(): void {
		// FetchesList on page-end while scrolling
		this.assetsDropdownScrollService.fetchListUpdate(this.pageSize);
	}

	public searchListUpdate(searchQuery: string = ""): void {
		// Deals with search-queries in the open dropdown
		this.searchInput$.next(searchQuery);
	}

	public getAPIPending(): boolean {
		return this.apiPending;
	}

	public getSwapOfferAPIData(): SwapOfferModel {
		return this.swapOfferAPIdata;
	}

	// ---------------------------------------

	public getTradableById(currencyId: string): Tradable {
		return this.tradableListService.getItemById(currencyId);
	}

	public get offerData(): SwapOfferModel {
		return this.swapOfferAPIdata;
	}

	public getRightTradableName(): string {
		const wallet: TradableWallet = this.tempRightSelection ?? <TradableWallet>this.rightSelection.currentWallet;
		if (wallet) {
			return this.tradableListService.getItemById(wallet.getCurrencyId()).getName();
		}
		return "";
	}

	private isNotDerivative(rightSelection): boolean {
		switch (rightSelection.getCurrencyType()) {
			case TRADABLES_TYPE.STOCK:
				return false;
			case TRADABLES_TYPE.ETF:
				return false;
			case TRADABLES_TYPE.ETC:
				return false;
			default:
				return true;
		}
	}

	private getTradable(wallet: Wallet): Tradable {
		if (!wallet) {
			return new Cryptocoin();
		}
		return this.tradableListService.getItemById(wallet.getCurrencyId()) || new Cryptocoin();
	}

	public sliderUpdate(e: number): void {
		this.inputChanged = true;
		this.amount = ((e * +this.getMaxAmount()) / 100).toString();
		this.sliderValueChanged.next();
	}

	public isTradable(wallet: Wallet): boolean {
		return wallet instanceof TradableWallet;
	}

	protected initDropdowns(): boolean {
		// Get wallets (mixed)
		this.loadWallets(this.leftSelection);
		this.loadWallets(this.rightSelection);

		this.refreshContentApplyFilters();

		// Order wallets by total value (fiat value). used only for the left dropdown
		this.sortWalletsByTotalValue(this.leftSelection);

		// Remove wallets with less amount than the minimum sell
		this.filterAmountsLessThanMinSell(this.leftSelection);

		this.leftSelection.bpSelectOptions = this.getDropdownElements(this.leftSelection, true);

		// Some event was triggered, that we dont have any wallets with legit balance => close and abort
		if (this.leftSelection.bpSelectOptions.length === 0) {
			this.close(true);
			return false;
		}
		return true;
	}

	public get isAtLeastDocumented(): boolean {
		return this.userService.isAtLeastDocumented;
	}

	public get isUnverified(): boolean {
		return this.userService.isUnverified;
	}

	public verify(): void {
		if (!this.verificationModalFacade.openVerification()) {
			this.router.navigate(VERIFICATION_MODAL_ROUTE, {
				skipLocationChange: true
			});
		}
	}

	private loadWallets(selection: SwapSelect): void {
		const walletsArray: Wallet[] = [];

		selection.loadedTypes.forEach((loadType) => {
			switch (loadType) {
				case SwapSelect.TYPE_CRYPTO: {
					const cryptoWallets = this.getWallets(selection);
					walletsArray.push(...cryptoWallets); // Add the elements by array join
					break;
				}
				case SwapSelect.TYPE_FIAT: {
					const fiatWallets = this.fiatWallets();
					walletsArray.push(...fiatWallets); // Add the elements by array join
					break;
				}
				default:
					break;
			}
		});
		selection.fullWalletsList = walletsArray;
	}

	private refreshContentApplyFilters(): void {
		// Get the filtered wallets as full arrays and then apply filters
		this.leftSelection.refreshFilteredWallets();
		this.rightSelection.refreshFilteredWallets();

		// Keep only the wallets which have money for the left dropdown
		this.leftSelection.applyFilterNonEmpty();

		// Keep only the default main wallets from each currency
		this.rightSelection.applyFilterOnlyDefault();

		//update taxable swap status
		this.updateTaxableSwapStatus();
	}

	// Tradable wallets
	public getWallets(selection: SwapSelect): TradableWallet[] {
		return this.walletService.list
			.filter((wallet) => {
				const tradable: Tradable = this.tradablesHash[wallet.getCurrencyId()];
				if (
					!tradable ||
					tradable.isIndexOnly() ||
					tradable instanceof LeveragedToken ||
					tradable instanceof SecurityToken
				) {
					return false;
				}

				if (selection.isLeftSelection) {
					return tradable.isSellActive() && !wallet.isDeleted();
				}

				if (selection.isRightSelection) {
					return tradable.isBuyActive() && !wallet.isDeleted();
				}

				return false;
			})
			.sort((wallet1, wallet2) => {
				const tradable1 = this.tradablesHash[wallet1.getCurrencyId()];
				const tradable2 = this.tradablesHash[wallet2.getCurrencyId()];
				return tradable1.getSort() - tradable2.getSort();
			});
	}

	private resetLeftSideAfterTrade(): void {
		// After a Swap in another tab, the left-side dropdown is updated.
		this.initDropdowns();
		const leftSelection: BpSelectOption =
			this.leftSelection.bpSelectOptions.find(
				(option) => option.value.getId() === this.leftSelection.currentWallet?.getId()
			) || null;
		if (leftSelection) {
			this.leftSelection.currentWallet = leftSelection.value;
		} else {
			// In case the wallet is no longer available, the selection is reset
			this.leftSelection.currentWallet = null;
		}
		this.setSwapReady();
		this.selectedLeftAssetId$.next(this.leftSelection.currentWallet?.getCurrencyId() || "");
		this.showAmountSection$.next(this.isSwapReady$.getValue());
	}

	// FIAT wallets
	public fiatWallets(): FiatWallet[] {
		return this.fiatwalletService.list;
	}

	// swap confirmation success screen helper
	private showSuccessScreen(data: SwapConfirmTrade[]): void {
		this.screenTrackhelper();

		this.launchDarklyService
			.isActive$(LD_FLAG.TRADE_SUCCESSFUL_SCREEN_REDESIGN)
			.subscribe((showRedesignSuccessScreen) => {
				this.modalService
					.create(showRedesignSuccessScreen ? SwapSuccessV1Component : SwapSuccessComponent, {
						swapSuccessTrades: data,
						visionFeeTokenCollection: this.vsnFeeCollection
					})
					.present();
			});
	}

	// swap pending screen helper
	private showPendingScreen(): void {
		this.screenTrackhelper();
		this.modalService
			.create(TradeSwapPendingComponent, {
				sourceAsset: this.tradableListService.getItemById(this.leftSelection.currentWallet?.getCurrencyId()),
				targetAsset: this.tradableListService.getItemById(this.rightSelection.currentWallet.getCurrencyId()),
				visionFeeTokenCollection: this.vsnFeeCollection
			})
			.present();
	}

	// Show taxes flow
	private showTaxesFlow(): void {
		this.taxStatusLoaded$.next(false);
		combineLatest([
			this.taxesFacade.isTaxesForTradeActive$,
			this.taxesFacade
				.fetchTaxStatusForTrade$(this.leftSelection.currentWallet.getId())
				.pipe(catchError((_) => of("error")))
		]).subscribe({
			next: ([isActive, taxStatus]) => {
				this.taxStatusLoaded$.next(true);
				const queryParams: Partial<TaxesQueryParamsCollection> = {
					[TaxesQueryParamName.LeftSelectionWalletId]: this.leftSelection.currentWallet.getId(),
					[TaxesQueryParamName.RightSelectionWalletId]: this.rightSelection.currentWallet.getId(),
					[TaxesQueryParamName.FromTradeFlow]: TaxesRouteNames.SwapTrade
				};
				const leftAsset = this.tradableListService.getItemById(
					this.leftSelection.currentWallet.getCurrencyId()
				);
				const rightAsset = this.tradableListService.getItemById(
					this.rightSelection.currentWallet.getCurrencyId()
				);

				// Check first if flag is active and swapping from crypto
				if (
					(isActive && leftAsset.getCurrencyType() === CRYPTO_TYPE) ||
					leftAsset.getCurrencyType() === INDEX_TYPE
				) {
					// Check is swapping to crypto or index
					if (rightAsset.getCurrencyType() === CRYPTO_TYPE || rightAsset.getCurrencyType() === INDEX_TYPE) {
						this.taxesFacade.openCryptoDeclarationTradeModal(
							{
								...queryParams,
								[TaxesQueryParamName.CryptoSwap]: "true"
							},
							taxStatus as TaxesUserStatusModel
						);
					} else {
						// In case of error reset right selection
						if (taxStatus === "error" && Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) !== "true") {
							// Reset the right selection
							this.rightSelection.currentWallet = null;
							this.setSwapReady();
							this.toastr.error(this.translationService.translate(ToastMessages.Error));
							return;
						}
						this.taxesFacade.openCryptoDeclarationTradeModal(
							queryParams,
							taxStatus as TaxesUserStatusModel
						);
					}
				}
			}
		});
	}

	// update the taxable swap status depending on the both selected wallets
	private updateTaxableSwapStatus(): void {
		if (!this.leftSelection.currentWallet || !this.rightSelection.currentWallet) {
			return;
		}

		const sourceAsset = this.tradableListService.getItemById(this.leftSelection.currentWallet.getCurrencyId());
		const sourceType = sourceAsset.getCurrencyType();

		const targetAsset = this.tradableListService.getItemById(this.rightSelection.currentWallet.getCurrencyId());
		const targetType = targetAsset.getCurrencyType();

		let swapStatus = false;

		const nonCryptoNonIndexTargetType = targetType !== CRYPTO_TYPE && targetType !== INDEX_TYPE;

		// taxable swap
		if (
			(sourceType === CRYPTO_TYPE && nonCryptoNonIndexTargetType) ||
			(sourceType === INDEX_TYPE && nonCryptoNonIndexTargetType)
		) {
			swapStatus = true;
		}

		// non taxable swap
		if (
			(sourceType === CRYPTO_TYPE && targetType === CRYPTO_TYPE) ||
			(sourceType === CRYPTO_TYPE && targetType === INDEX_TYPE) ||
			(sourceType === INDEX_TYPE && targetType === CRYPTO_TYPE) ||
			(sourceType === INDEX_TYPE && targetType === INDEX_TYPE)
		) {
			swapStatus = false;
		}

		// reset tax withheld summary
		if (sourceType === INDEX_TYPE) {
			this.taxWithheldBS$.next(null);
		} else {
			this.taxWithheldBS$.next(this.taxWithheldDefault(swapStatus));
		}

		// update taxable swap status
		this.taxableSwapStatus$.next(swapStatus);
	}

	// get tax withheld default value which is the reset to the initial state
	private taxWithheldDefault(swapStatus: boolean): ITaxSwap | null {
		if (!swapStatus || this.taxStatus !== "ELIGIBLE") {
			return null;
		}

		const tax: Partial<ITaxWithheld> = {
			isTaxServiceAvailable: true,
			fiatId: this.defaultFiat.getId(),
			assetId: this.rightSelection.currentWallet?.getCurrencyId(),
			taxAmountFiat: "0",
			taxAmountAsset: "0",
			fiatAmountAfterTax: "0"
		};

		return {
			tax,
			is_taxable: true
		};
	}

	// screen track helper
	private screenTrackhelper(): void {
		this.success = true;
		this.trackSuccess();
		super.close();
	}

	private getBalance(walletBalance: string, isToken: boolean): string {
		const tokenValue = changeTokenUnit(walletBalance);
		const tokenBalance = walletBalance + " " + this.translationService.translate(tokenValue);
		return isToken ? tokenBalance : walletBalance;
	}

	public getDropdownElements(
		list: SwapSelect,
		showBalance: boolean = false,
		useTradableName: boolean = false
	): BpSelectOption[] {
		return list.filteredWalletsList.map((wallet) => {
			let balance = wallet.getBalance();
			if (this.isIndex(wallet)) {
				balance = this.toDefaultFiat(balance);
			}

			const precision = this.isIndex(wallet)
				? this.defaultFiat.getPrecision()
				: this.getWalletCurrency(wallet).getPrecision();
			const symbol = this.isIndex(wallet)
				? this.defaultFiat.getSymbol()
				: this.getWalletTradable(wallet).getSymbol();
			let walletBalance = truncateFixed(balance, precision) + " " + symbol;

			const tradable = this.getTradable(wallet);
			if (tradable.isToken()) {
				walletBalance = truncateFixed(balance, precision);
			}

			// Below the display name for 3 cases: Wallets are shown, asset is shown and name is below 17 chars, asset is shown and name is longer than 17 chars
			const useWalletName: boolean = !useTradableName && this.multipleWalletsDisplay(wallet);
			let displayName: string;
			if (useWalletName) {
				displayName = wallet.getName();
			} else {
				// In case wallet name should be used
				const walletCurrency: Currency = this.getWalletCurrency(wallet);
				// Below checks length of currencyName -> if too long, symbol is used instead
				displayName =
					walletCurrency.getName().length > MAX_CHARACTERS_FOR_DISPLAY
						? walletCurrency.getSymbol()
						: walletCurrency.getName();
			}

			if (tradable.isDerivative()) {
				displayName += " (" + this.translationService.translate("legacy") + ")";
			}

			return {
				name: displayName,
				iconUrl: this.getTradable(wallet).getLogo(),
				value: wallet,
				details: showBalance ? this.getBalance(walletBalance, this.getTradable(wallet).isToken()) : ""
			};
		});
	}

	public multipleWalletsDisplay(wallet: Wallet): boolean {
		return this.walletService.getWalletsByTradableId(this.getWalletCurrency(wallet).getId()).length > 1;
	}

	public getIconId(wallet: Wallet): string {
		let iconId: string;
		const walletCurrency: Currency = this.getWalletCurrency(wallet);
		if (wallet instanceof TradableWallet) {
			iconId = walletCurrency.getIconCssName(true);
		} else if (wallet instanceof FiatWallet) {
			iconId = "bpi__asset-" + walletCurrency.getSymbol(true);
		}
		return iconId;
	}

	public getWalletCurrency(wallet: Wallet): Currency {
		let currency: Currency;
		if (wallet instanceof TradableWallet) {
			const tradableId = wallet.getCurrencyId();
			currency = this.tradableListService.getItemById(tradableId);
		} else if (wallet instanceof FiatWallet) {
			const fiatId = wallet.getFiatId();
			currency = this.fiatService.getItemById(fiatId);
		}
		return currency;
	}

	// Currency shown in the right part of the input switcher (Eg: BTC / ETH )
	public getSwitchCurrencySymbol(wallet: Wallet): string {
		const currency: Tradable = this.getWalletTradable(wallet);
		if (currency) {
			if (currency instanceof Index) {
				return this.defaultFiat.getSymbol();
			}

			return currency.getSymbol();
		}
		return "";
	}

	public getSwitchIcon(wallet: Wallet): string {
		const currency: Tradable = this.getWalletTradable(wallet);
		if (currency) {
			if (currency instanceof Index) {
				return this.defaultFiat.getLogo();
			}
			return currency.getLogo();
		}
		return "";
	}

	public hasSelectedToken(): boolean {
		return (
			this.getTradable(this.leftSelection.currentWallet).isToken() ||
			this.getTradable(this.rightSelection.currentWallet).isToken()
		);
	}

	public getWalletTradable(wallet: Wallet): Tradable {
		let currency: Tradable;
		if (wallet instanceof TradableWallet) {
			const tradableId = wallet.getCurrencyId();
			currency = this.tradableListService.getItemById(tradableId);
		}
		return currency;
	}

	public getCurrentSelectedIcon(): string {
		const w: Wallet = this.switchedInputCurrencies
			? this.rightSelection.currentWallet
			: this.leftSelection.currentWallet;
		return this.getTradable(w).getLogo();
	}

	public setSwapReady(): void {
		//reset fee transparency
		this.updateFeeTransparency();

		this.isSwapReady$.next(!!this.rightSelection.currentWallet && !!this.leftSelection.currentWallet);
	}

	private getTotalAssetAmount(): number {
		const currentTradableId: string = this.rightSelection.currentWallet?.getCurrencyId() || "";
		return (
			this.walletService
				.getWalletsByTradableId(currentTradableId)
				?.reduce((val: number, w: TradableWallet) => val + w.getBalance(), 0) || 0
		);
	}

	// TODO: Remove after 03.01.2022
	public onDropdownValueChange(wallet: TradableWallet, side: string): void {
		this.focusChanged = false;
		this.inputChanged = false;
		this.setSwapReady();
		this.showDefaultError = false; // Resetting default error in case API call failed
		if (
			this.termsConditionsFf === false &&
			wallet.getCurrencyId() === TOKEN_BEST_ID &&
			this.accountService.showBest2TermsOnBestTrade()
		) {
			if (side === "right") {
				this.tempRightSelection = wallet;
				this.setCurrentTradable();
				this.handleBest2Terms(wallet, side);
			}
		} else {
			if (side === "left") {
				this.stakedTradeFacade.fetchStakeableDataById$(this.leftSelection.currentWallet.getCurrencyId());
			}
			this.dropdownValueUpdate(wallet, side);
		}
		this.totalAssetAmount = "" + this.getTotalAssetAmount();
		this.setNotDerivativeTradable();
	}

	private setNotDerivativeTradable(): void {
		const leftSelectionTrade = this.tradableListService.getItemById(
			this.leftSelection.currentWallet?.getCurrencyId()
		)
			? this.tradableListService.getItemById(this.leftSelection.currentWallet?.getCurrencyId())
			: undefined;

		const rightSelectionTrade = this.tradableListService.getItemById(
			this.rightSelection.currentWallet?.getCurrencyId()
		)
			? this.tradableListService.getItemById(this.rightSelection.currentWallet?.getCurrencyId())
			: undefined;

		if (leftSelectionTrade && rightSelectionTrade) {
			this.tradable = this.isNotDerivative(rightSelectionTrade) ? leftSelectionTrade : rightSelectionTrade;
		}
	}

	// TODO: Remove after 03.01.2022
	public handleBest2Terms(wallet: TradableWallet, side: string): void {
		this.modalService.getCurrentModal().hide();
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.dropdownValueUpdate(wallet, side);
				if (side === "right") {
					this.setCurrentTradable();
					this.tempRightSelection = null;
				}
			} else {
				if (side === "left") {
					this.leftSelection.currentWallet = this.leftSelection.bpSelectOptions.find(
						(wallet) => wallet.name !== BEST_SYMBOL.toUpperCase()
					).value;
				} else {
					this.rightSelection.currentWallet = this.assetsDropdownScrollService
						.getListValue()
						.find((wallet) => wallet.name !== BEST_SYMBOL.toUpperCase()).value;
					this.setCurrentTradable();
				}
			}
		});
	}

	public dropdownValueUpdate(wallet: TradableWallet, side: string): void {
		if (!this.isSwapReady$.getValue()) {
			// Subject swapReady is updated on every dropdown change, but not every change should ripple all the way through
			// Left selection assetId should always be updated though to show the staking warning correctly
			if (this.leftSelection.currentWallet) {
				this.selectedLeftAssetId$.next(this.leftSelection.currentWallet.getCurrencyId());
			}
			return;
		}
		this.sliderValue = 0;
		this.amount = "0";
		this.sliderPercent = "0% ";

		if (wallet && side === "left") {
			// If the left wallet is different from the previous one, refetch tax-status
			this.leftSelection.currentWallet = wallet;
			this.selectedLeftAssetId$.next(wallet.getCurrencyId());
		} else if (wallet && side === "right") {
			this.rightSelection.currentWallet = wallet;
			this.setCurrentTradable();
		}

		// Remove each selected currency from the other dropdown list
		this.refreshContentApplyFilters();

		// Order wallets by total value (fiat value). used only for the left dropdown
		this.sortWalletsByTotalValue(this.leftSelection);

		// Remove wallets with less amount than the minimum sell
		this.filterAmountsLessThanMinSell(this.leftSelection);

		// Remove the stocks warnings
		this.resetStockWarnings();
		this.checkForStockTest();
		this.getHodlCoinInfo();

		this.leftSelection.bpSelectOptions = this.getDropdownElements(this.leftSelection, true);
		this.getDropdownPricesAPIData();
		this.swapToggles?.disableSliders();
	}

	// The external options arrive later than TradeSwapComponent creation.
	// The Modal.service does an Object.assign with this options to and only after it creates instance of TradeSwapComponent
	public set externalLaunchOptions(value: ExternalLauchOption) {
		if (value.wallet) {
			this._externalLaunchOptions = value;
		} else if (value.id) {
			let wallet: TradableWallet;
			if (value.usedFor === "left") {
				wallet = this.walletService.getWalletsByTradableId(value.id).find((w) => {
					const tradable: Tradable = this.tradableListService.getItemById(value.id);
					if (!tradable) {
						return false;
					}
					return (
						w.isDefault() &&
						convertTradableToFiat(tradable, w.getBalance(), this.fiatService.getItemById("1")) >=
							tradable.getMinSell()
					);
				});
			} else {
				wallet = this.walletService.getWalletsByTradableId(value.id).find((w) => w.isDefault());
			}
			this._externalLaunchOptions = { wallet: wallet };
		}
		this._externalLaunchOptions.usedFor = value.usedFor ? value.usedFor : "left";
		this.setDefaultSelectedWallets();
	}

	public get externalLaunchOptions(): ExternalLauchOption {
		return this._externalLaunchOptions;
	}

	// This takes the initial selected values. Eg: BTC -> ETH or any -> BTC
	private setDefaultSelectedWallets(): void {
		// Check Swap launch with pre-select option 'left'
		if (this.externalLaunchOptions && this.externalLaunchOptions.usedFor === "left") {
			this.leftSelection.currentWallet = this.getExternalOptionsWallet(this.leftSelection);
		}

		// Check Swap launch with pre-select option 'right'
		if (this.externalLaunchOptions && this.externalLaunchOptions.usedFor === "right") {
			if (
				this.externalLaunchOptions.wallet.getCurrencyId() === this.leftSelection.currentWallet?.getCurrencyId()
			) {
				this.leftSelection.currentWallet = this.leftSelection.filteredWalletsList[1];
			}
			this.rightSelection.currentWallet = this.getExternalOptionsWallet(this.rightSelection);
		}
		// From twflow set the right side wallet
		if (this.externalLaunchOptions && this.externalLaunchOptions.usedFor2 === "right" && this.fromTWFlow) {
			this.rightSelection.currentWallet = this.getExternalOptionsWallet2(this.rightSelection);
		}
	}

	private getExternalOptionsWallet(selection: SwapSelect): Wallet {
		return selection.filteredWalletsList.find(
			(wallet) => wallet.getId() === this.externalLaunchOptions.wallet.getId()
		);
	}

	private getExternalOptionsWallet2(selection: SwapSelect): Wallet {
		return selection.filteredWalletsList.find(
			(wallet) => wallet.getId() === this.externalLaunchOptions.wallet2.getId()
		);
	}

	// This referres to the amount switched inside the input and not the switch between dropdowns lists
	public switchedInputCurrencies: boolean = false;

	public switchCurrencies(): void {
		if (this.isSameCurrency()) {
			return;
		}
		this.switchedInputCurrencies = !this.switchedInputCurrencies;
		this.updateFields();
		// this.getApiSwapOffer();
	}

	public getInputFormat(): string {
		if (this.switchedInputCurrencies) {
			return this.getBpTypeFormat(this.rightSelection.currentWallet);
		} // Else
		return this.getBpTypeFormat(this.leftSelection.currentWallet);
	}

	private getBpTypeFormat(wallet: Wallet): string {
		// Formats according to bp-type.directive
		if (wallet instanceof TradableWallet) {
			return "cryptocoin";
		}

		if (wallet instanceof FiatWallet) {
			return "fiat";
		}
		return "";
	}

	private _amount: string = "";

	public get amount(): string {
		return this._amount;
	}

	public set amount(value: string) {
		// Needed for non-EN inputs
		this._amount = stripFunnyInput(value);
		this.updateTotalFromAmountChange();

		if (+this.amount > 0) {
			const percentValue: number = truncate((+this.amount * 100) / +this.getMaxAmount(), 0);
			this.sliderValue = percentValue > 100 ? 100 : percentValue;
		} else {
			this.sliderValue = 0;

			//part of TW flow update teh default value if value is 0
			this.updateTaxableSwapStatus();
		}

		this.amountTooLow = +this.amount < +this.getMinAmount();
		this.amountExceedsBalance = +this.amount > +this.getMaxAmount();
		this.hasInputError = this.amountTooLow || this.amountExceedsBalance;

		// update slider behaviour subject
		this.sliderValueChanged.next();
	}

	public getSliderValue(): number {
		return this.sliderValue;
	}

	// Sort the wallets according to their total value
	public sortWalletsByTotalValue(selection: SwapSelect): void {
		selection.filteredWalletsList = selection.filteredWalletsList.sort(
			(wallet1, wallet2) => this.amountConvertedToFiat(wallet2) - this.amountConvertedToFiat(wallet1)
		);
	}

	// Total wallet amount inf Default Fiat
	public amountConvertedToFiat(wallet: Wallet): number {
		let convertedAmount: number = 0;
		if (wallet instanceof TradableWallet) {
			const tradable: Tradable = this.tradableListService.getItemById(wallet.getCurrencyId());
			if (!this.defaultFiat) {
				return 0;
			}
			let amount = (tradable.getPrice() * wallet.getBalance()) / this.defaultFiat.toEuroRate();
			if (tradable instanceof Index) {
				amount = wallet.getBalance();
			}
			convertedAmount = +amount.toFixed(this.defaultFiat.getPrecision());
		} else {
			if (wallet instanceof FiatWallet) {
				// TODO for next iteration: transform to default fiat (eg USD -> EUR)
			}
		}
		return convertedAmount;
	}

	public get defaultFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public toDefaultFiat(balance: number): number {
		if (!balance) {
			return 0;
		}
		return toCurrencyByFiatId(balance, this.defaultFiat);
	}

	private filterAmountsLessThanMinSell(selection: SwapSelect): void {
		selection.filteredWalletsList = selection.filteredWalletsList.filter((wallet) => {
			if (wallet instanceof TradableWallet) {
				return this.amountConvertedToFiat(wallet) >= (this.getWalletCurrency(wallet) as Tradable).getMinSell();
			}
			return true;
		});
	}

	private getHodlCoinInfo(): void {
		if (!this.rightSelection || !this.rightSelection.currentWallet) {
			return;
		}
		const t = this.getTradable(this.rightSelection.currentWallet);
		if (t) {
			this.assetInfoService.fetchApiInfo(t);
		}
	}

	/*
		API call - receive prices data
	  */
	private lastDropdownApiCallOptions: ApicallOption;

	private getDropdownPricesAPIData(): void {
		this.showConversionRate = false;
		this.tradeSwapFacade.isSwapEstimateGQLEnabled$.subscribe((ff) => {
			//new estimate GraphQL API
			if (ff) {
				const input = this.tradeSwapFacade.getSourceTargetPid(
					this.leftSelection.currentWallet,
					this.rightSelection.currentWallet
				);
				this.tradeSwapFacade.getSwapEstimateGQL$(input).subscribe({
					next: (res) => {
						this.defaultPricesAPIdata = res;

						this.estimateRespondsHelper();
					},
					error: (error) => {
						this.estimateErrorHelper(error);
					}
				});
			} else {
				//old estimate REST API
				this.getEstimateRestAPIData();
			}
		});
	}

	private getEstimateRestAPIData(): void {
		const apiOptions = new ApicallOption();
		apiOptions.data = this.getSourceTargetIds();

		// Don't duplicate the same call when there are no changes in the dropdown lists from the last call
		if (equal(this.lastDropdownApiCallOptions, apiOptions)) {
			return;
		}
		this.lastDropdownApiCallOptions = apiOptions;

		// this.showConversionRate = false;
		this.apicallService.post("swap/prices/default", apiOptions).subscribe({
			next: (res) => {
				this.defaultPricesAPIdata = parseObject(res, SwapDefaultPricesModel);
				this.estimateRespondsHelper();
			},
			error: (error) => {
				this.estimateErrorHelper(error);
			}
		});
	}

	private estimateRespondsHelper(): void {
		const amountWasMax: boolean = this.checkAmountIsMax(); // Used to set automatically the amount to Max, if user changes wallets (and had max amount before)

		// set the Appropriate Test warning flag
		this.appTestTradingWarning = this.defaultPricesAPIdata.getAppTestTradingWarning();

		this.setConversionRateOneUnit();
		this.showConversionRate = true;
		this.showAmountSection$.next(true);

		this.checkAmountBiggerThanMax();
		if (amountWasMax) {
			this.setMaxAmount();
			//this.getApiSwapOffer();
		}

		this.getApiSwapOffer();

		// check if TW flow on false
		if (!this.fromTWFlow) {
			this.showTaxesFlow();
		}
		this.userJourneys = [this.defaultPricesAPIdata.getUserJourney()];
		const tradableType = this.getTradable(this.rightSelection.currentWallet).getCurrencyType();
		if (tradablesWithoutAppropriateness.indexOf(tradableType) === -1 && this.userService.user.needsRiskToggle()) {
			this.userJourneys.push(UserJourney.AppropriatenessWarning);
		}
		this.bundles$ = this.tCService
			.getBundlesRemote(this.userJourneys)
			.pipe(tap((bundles) => (this.bundles = bundles)));
	}

	private estimateErrorHelper(error: Error | string): void {
		Helper.handleError(error);
		this.amount = "0";
		this.showDefaultError = true;

		// trigger appropriateness test if code return match appr_test is required
		if (typeof error === "string" && error.includes(APPROPRIATENESS_REQUIRED_ERROR_CODE)) {
			this.stockTestHelperService.startTest(null, true);
		}
		this.isSwapReady$.next(false);
		this.showAmountSection$.next(false);
		if (error instanceof OfflineError) {
			this.modalService.create(OfflineModalComponent).present();
		}
	}

	public showErrorSection(): boolean {
		return this.showDefaultError || this.isSameCurrency();
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getSourceTargetIds(): any {
		const sourceWallet: TradableWallet = this.walletService.getItemById(this.leftSelection.currentWallet.getId());

		// Asset id:
		if (sourceWallet.isDefault()) {
			return {
				source_asset_id: this.leftSelection.currentWallet.getCurrencyId(),
				target_asset_id: this.rightSelection.currentWallet?.getCurrencyId()
			};
		}
		// Wallet id:
		return {
			source_wallet_id: this.leftSelection.currentWallet.getId(),
			source_asset_id: this.leftSelection.currentWallet.getCurrencyId(),
			target_asset_id: this.rightSelection.currentWallet.getCurrencyId()
		};
	}

	private handleGraphqlOfferSuccess(graphqlOffer: SwapOfferCreateMutation): void {
		if (!graphqlOffer.swapOfferCreate) {
			return;
		}

		const { expiresAt, serverTime, swapOfferId } = graphqlOffer.swapOfferCreate.swapOffer;

		//push taxWithheld object to rxjs stream
		this.taxWithheldBS$.next(this.desirealizeTaxFromGqlOffer(graphqlOffer));
		this.updateFeeTransparencyGql(graphqlOffer);
		this.estimateLoading = false;
		this.updateTotalFromGraphql(graphqlOffer);
		this.startGraphqlOfferCountdown(expiresAt.unixTimestamp, serverTime.unixTimestamp);
		this.swapOfferId = swapOfferId;
		// Set vsnFeeBenefit model data
		this.updateVsnFeeBenefitModelData(graphqlOffer);
	}

	private updateVsnFeeBenefitModelData(graphqlOffer: SwapOfferCreateMutation): void {
		const { vsnFeeTokenCollection } = graphqlOffer.swapOfferCreate.swapOffer;

		// Set vsnFeeBenefit model data
		const vsnFeeCollection: IVSNFeeTokenCollection = {
			globalOptIn: vsnFeeTokenCollection?.globalOptIn,
			enabled: vsnFeeTokenCollection?.enabled,
			eligibleForVsn: vsnFeeTokenCollection?.eligibleForVsn,
			feeDiscount: vsnFeeTokenCollection?.feeDiscount,
			visionFeeDetail: vsnFeeTokenCollection?.visionFeeDetail
		};

		this._visionBenefitToggleTexts.heading.discount = +vsnFeeTokenCollection?.feeDiscount;
		this.vsnFeeBenefit = { vsnFeeCollection, texts: this._visionBenefitToggleTexts };

		// Set vsnFeeBenefit Total model data
		const sourceTradable = this.tradableListService.getItemById(this.leftSelection.currentWallet.getCurrencyId());
		const targetTradable = this.tradableListService.getItemById(this.rightSelection.currentWallet.getCurrencyId());
		const sourceTotal = this.swapFee?.sourceAmount;
		const targetTotal = this.swapFee?.targetAmount;
		const fiat = this.userFiat;

		this.vsnBenefitTotalVM = {
			total: this.getTotalReceived() as string,
			sourceTradable,
			targetTradable,
			sourceTotal,
			targetTotal,
			fiat,
			vsnFeeCollection: vsnFeeCollection
		};

		// Set vsnFeeCollection model data
		this.vsnFeeCollection = vsnFeeCollection;
	}

	onVSNToggle(state: boolean): void {
		this._bonusFlag = state;
		this.getApiSwapOffer();
	}

	private handleOfferCreationSuccess(swapOffer: SwapOfferModel): void {
		swapOffer.source_wallet = this.walletService.getItemById(swapOffer.source_wallet_id);
		swapOffer.target_wallet = this.walletService.getItemById(swapOffer.target_wallet_id);
		swapOffer.bestFeeOptIn = this.swapOfferAPIdata.bestFeeOptIn;
		swapOffer.setRequestedFor(this.getDefinedFor());
		swapOffer.setUpdateFinished(true);
		//push taxWithheld object to rxjs stream
		this.taxWithheldBS$.next(this.diserializeTaxFromAPI(swapOffer));

		this.swapOfferAPIdata = swapOffer;
		//update fee transparency
		this.updateFeeTransparency(swapOffer);

		this.estimateLoading = false;
		this.updateTotalFromAPIOfferResponse();
		this.startCountingTermsDeactivate(swapOffer);
	}

	private handleSwapOfferError(error: Error): void {
		// Reset the target asset selection
		this.estimateLoading = false;
		this.taxWithheldBS$.next(null);
		this.setSwapReady();

		Helper.handleError(error);
		clearInterval(this.oldTimer); // Cleaning
	}

	private getOfferFromGraphql(): void {
		const amount = setPrecisionDecimals(this.amount, PRECISION_FOR_TRANSACTION);
		const amountDefinedFor = this.switchedInputCurrencies
			? SwapAmountDefinedFor.Target
			: SwapAmountDefinedFor.Source;
		const sourceAssetId = this.tradableListService.getItemById(this.getSourceTargetIds().source_asset_id).getPid();
		const targetAssetId = this.tradableListService.getItemById(this.getSourceTargetIds().target_asset_id).getPid();
		this.tradeSwapService
			.swapOffer(amount, amountDefinedFor, sourceAssetId, targetAssetId, this._bonusFlag)
			.subscribe({
				next: (data) => {
					this.handleGraphqlOfferSuccess(data);
				},
				error: (error) => {
					this.handleSwapOfferError(error);
				}
			});
	}

	private getRestApiOffer(): void {
		const apiOptions = new ApicallOption(SwapOfferModel, {
			...this.getSourceTargetIds(),
			amount: setPrecisionDecimals(this.amount, PRECISION_FOR_TRANSACTION),
			amount_defined_for: this.getDefinedFor()
		});
		this.swapOfferAPIdata.setUpdateFinished(false);

		this.apicallService
			.post("swap/offer", apiOptions)
			.pipe(map((a) => a[0]))
			.subscribe({
				next: (swapOffer: SwapOfferModel) => {
					this.handleOfferCreationSuccess(swapOffer);
				},
				error: (error) => {
					this.handleSwapOfferError(error);
				}
			});
	}

	/*
		API call - take offer when user inputs something or when it switched the currencies
	  */
	private getApiSwapOffer(): void {
		this.acceptOfferPending = false;
		if (!this.amount || !+this.amount || +this.amount === 0) {
			return;
		}

		if (
			this.swapGraphqlFlag &&
			!this.isIndex(this.leftSelection.currentWallet) &&
			!this.isIndex(this.rightSelection.currentWallet)
		) {
			this.getOfferFromGraphql();
		} else {
			this.getRestApiOffer();
		}
	}

	private updateFeeTransparencyGql(offerFromGql: SwapOfferCreateMutation = null): void {
		if (offerFromGql) {
			//set fee transparency
			const offer = offerFromGql.swapOfferCreate.swapOffer;
			this.isFeeTransparent = offer.isFeeTransparent;
			let fees: ISwapFee | null = null;
			if (this.isFeeTransparent && offer.fee) {
				fees = {
					feeAmount: offer.fee.amount.value,
					netValue: offer.targetTrade.amountAsset.value,
					swapAmount: offer.fee.source.value,
					sourceAmount: offer.fee.source.value,
					targetAmount: offer.fee.target.value,
					fiatId: offer.fee.fiatId
				};
			}
			this.swapFee = fees;
		} else {
			this.isFeeTransparent = false;
			this.swapFee = null;
		}
	}

	private updateFeeTransparency(swapOffer: SwapOfferModel = null): void {
		if (swapOffer) {
			//set fee transparency
			this.isFeeTransparent = swapOffer.getIsFeeTransparent();
			this.swapFee = swapOffer.getSwapFee();
		} else {
			this.isFeeTransparent = false;
			this.swapFee = null;
		}
	}

	private desirealizeTaxFromGqlOffer(offerFromGql: SwapOfferCreateMutation): ITaxSwap {
		const tax = offerFromGql.swapOfferCreate.swapOffer?.taxWithheld;
		let taxWithheld: Partial<ITaxWithheld> = null;

		taxWithheld = {
			isTaxServiceAvailable: tax?.isTaxServiceAvailable,
			fiatId: tax?.fiatId,
			assetId: tax?.assetId,
			taxAmountFiat: tax?.taxAmountFiat?.value,
			taxAmountAsset: tax?.taxAmountAsset?.value,
			fiatAmountAfterTax: tax?.fiatAmountAfterTax?.value
		};
		return {
			tax: taxWithheld,
			is_taxable: offerFromGql.swapOfferCreate.swapOffer?.isTaxable
		};
	}

	private diserializeTaxFromAPI(data: SwapOfferModel): ITaxSwap {
		let taxWithheld: Partial<ITaxWithheld> = null;

		const tax: ITaxSwapAPIModel | null = data["tax"];
		const is_taxable: boolean = data["is_taxable"];

		if (tax?.attributes) {
			const attr = tax.attributes;

			taxWithheld = {
				isTaxServiceAvailable: attr?.is_tax_service_available,
				fiatId: attr?.fiat_id,
				assetId: attr?.asset_id,
				taxAmountFiat: attr?.tax_amount_fiat,
				taxAmountAsset: attr?.tax_amount_asset,
				fiatAmountAfterTax: null
			};
		}

		return {
			tax: taxWithheld,
			is_taxable
		};
	}

	/*
		API call - send the Swap user's confirmation to API
	*/

	public confirmSwapToAPI(): void {
		if (this.isButtonDisabled || this.acceptOfferPending || this.estimateLoading) {
			return;
		}
		if (this.termsConditionsFf && this.bundles.length > 0) {
			if (this.dynamicTermsInvalid) {
				this.dynamic_terms.wiggleIt();
			} else {
				this.acceptOfferPending = true;
				this.tCService.acceptTerms(this.userJourneys, this.dynamic_terms?.termsAndConditionsForm?.value, () => {
					this.continue();
				});
			}
		} else {
			this.continue();
		}
	}

	public continue(): void {
		if (!this.isSwapReady$.getValue()) {
			this.infoService.setError(this.translationService.translate("swap_asset_selection_error"));
			this.acceptOfferPending = false;
			return;
		}

		if (!this.termsConditionsFf) {
			this.swapTogglesLegacy();
		}

		this.acceptOfferPending = true;
		this.apiPending = true;

		if (
			this.swapGraphqlFlag &&
			!this.isIndex(this.leftSelection.currentWallet) &&
			!this.isIndex(this.rightSelection.currentWallet)
		) {
			this.acceptSwapOfferHelper();
		} else {
			this.legacyGraphqlHelper();
		}
	}

	private swapTogglesLegacy(): void {
		if (!this.swapToggles) {
			return;
		}
		if (this.swapToggles?.isButtonDisabled()) {
			if (this.isSameCurrency()) {
				this.infoService.setError(
					this.translationService.translate("info_text_same_cryptocurrency_cannot_be_swapped")
				);
				return;
			}
			this.swapToggles?.validateSliders();

			this.infoService.setError(this.translationService.translate("info_text_offer_checkboxes_not_checked"));
			return;
		}
	}

	private acceptSwapOfferHelper(): void {
		// Only pass in wallet ids if they are non-default
		const sourceWallet: TradableWallet = this.walletService.getItemById(this.leftSelection.currentWallet.getId());
		const sourceWalletId = sourceWallet.isDefault() ? undefined : sourceWallet.getId();

		this.tradeSwapService
			.acceptSwapOffer(this.swapOfferId, sourceWalletId)
			.pipe(take(1))
			.subscribe({
				next: (data) => {
					// Necessary to show Vision warning on Success screen
					if (
						this.getTradable(this.rightSelection.currentWallet).getCurrencyType() === TRADABLES_TYPE.CRYPTO
					) {
						this.stakingFacade.updateFilteredAssets("", "");
					}

					const swapObj = data.swapOfferAccept as SwapOfferAcceptSuccessResponse;
					// in case swap is successful, return null (on purpose) becasuse the type is "SwapInProgressResponse" then it should redirect to the pending screen
					if (!swapObj?.swapOffer) {
						this.showPendingScreen();
						return;
					}
					const sellWalletresultId = swapObj.swapOffer.sourceTrade.assetWallet.id;
					const buyWalletResultId = swapObj.swapOffer.targetTrade.assetWallet.id;

					// process confirmation for 'buy' and 'sell'
					this.processSwapConfirmation(sellWalletresultId, this.leftSelection.currentWallet);
					this.processSwapConfirmation(buyWalletResultId, this.rightSelection.currentWallet);

					// open success modal
					this.showSuccessScreen(
						this.tradeSwapService.swapResponseAdapter(
							this.tradeSwapService.convertResponseIntoCompatible(swapObj)
						)
					);

					// stop counter
					clearInterval(this.oldTimer);
				},
				error: (error) => {
					Helper.handleError(error);
					this.swapToggles?.disableSliders();
					// stop counter
					clearInterval(this.oldTimer);
				}
			})
			.add(() => (this.apiPending = false));
	}

	// Swap process using new GRAPHQL API
	private legacyGraphqlHelper(): void {
		const amount = setPrecisionDecimals(this.amount, PRECISION_FOR_TRANSACTION);

		const amountDefinedFor = this.switchedInputCurrencies
			? SwapAmountDefinedFor.Target
			: SwapAmountDefinedFor.Source;
		const sourceAssetId = this.getSourceTargetIds().source_asset_id;
		const targetAssetId = this.getSourceTargetIds().target_asset_id;
		const sourceWalletId = this.leftSelection.currentWallet.isNew() ? "" : this.leftSelection.currentWallet.getId();
		const targetWalletId = this.rightSelection.currentWallet.isNew()
			? ""
			: this.rightSelection.currentWallet.getId();

		this.tradeSwapService
			.swapAssetGQL(amount, amountDefinedFor, sourceAssetId, targetAssetId, sourceWalletId, targetWalletId)
			.pipe(take(1))
			.subscribe({
				next: (data) => {
					// in case swap is successful, return null (on purpose) becasuse the type is "SwapInProgressResponse" then it should redirect to the pending screen
					if (!data) {
						this.showPendingScreen();
						return;
					}

					const swapObj = data.swap as SwapSuccessResponse;
					const sellWalletresultId = swapObj.swap.sourceTrade.wallet.id;
					const buyWalletResultId = swapObj.swap.targetTrade.wallet.id;

					// Necessary to show Vision warning on Success screen
					if (
						this.tradableListService.getItemById(swapObj.swap?.targetTrade?.tradeId)?.getCurrencyType() ===
						TRADABLES_TYPE.CRYPTO
					) {
						this.stakingFacade.updateFilteredAssets("", "");
					}

					// process confirmation for 'buy' and 'sell'
					this.processSwapConfirmation(sellWalletresultId, this.leftSelection.currentWallet);
					this.processSwapConfirmation(buyWalletResultId, this.rightSelection.currentWallet);

					// open success modal
					this.showSuccessScreen(this.tradeSwapService.swapResponseAdapter(swapObj));

					// stop counter
					clearInterval(this.oldTimer);
				},
				error: (error) => {
					Helper.handleError(error);
					this.swapToggles?.disableSliders();
				}
			})
			.add(() => (this.apiPending = false));
	}

	// Post step after swap confirmation
	private processSwapConfirmation(walletID: string, wallet: Wallet): void {
		const service = this.walletService.serviceForId(wallet.getId());
		if (!service) {
			return;
		}
		const walletCall = new ApicallOption(service?.WalletType);
		this.apicallService.get("wallet/" + walletID, walletCall).subscribe((a) => {
			service.upsert(a[0]);
			// Remove the auto-created wallet, as new (real) wallet for this tradable was created by API
			if (wallet.isNew() && wallet.getCurrencyId() === a[0].getCurrencyId()) {
				const w = service?.getItemById(wallet?.getId());
				service.deleteItem(w);
			}
		});
		service.transactionReceived();
	}

	private getDefinedFor(): string {
		return this.switchedInputCurrencies ? "target" : "source";
	}

	private setConversionRateOneUnit(): void {
		let exchangeRate: string;
		if (this.switchedInputCurrencies) {
			exchangeRate = this.defaultPricesAPIdata.target.unit.target_amount;
		} else {
			exchangeRate = this.defaultPricesAPIdata.source.unit.target_amount;
		}
		const currentSelection = this.currentSwitchInputSelect;
		if (currentSelection.currentWallet instanceof TradableWallet) {
			this.conversionRateOneUnit = exchangeRate || "" + 1 / +this.conversionRateOneUnit;
		} else if (currentSelection.currentWallet instanceof FiatWallet) {
			this.conversionRateOneUnit = exchangeRate || "" + 1 / +this.conversionRateOneUnit;
		}
	}

	public get currentSwitchInputSelect(): SwapSelect {
		if (this.switchedInputCurrencies) {
			return this.rightSelection;
		}
		return this.leftSelection;
	}

	public get switchInputSelections(): SwapSelect[] {
		if (this.switchedInputCurrencies) {
			return [this.rightSelection, this.leftSelection];
		}

		return [this.leftSelection, this.rightSelection];
	}

	private updateSwitchedAmount(): void {
		let newAmount: number;
		if (this.switchedInputCurrencies) {
			newAmount = +this.amount * +this.defaultPricesAPIdata.source.unit.target_amount;
		} else {
			newAmount = +this.amount * +this.defaultPricesAPIdata.target.unit.target_amount;
		}
		this.amount = "" + newAmount;
	}

	public getTotalReceived(getEquivalent: boolean = false): string | number {
		return getEquivalent
			? this.totalReceivedEquivalent || +this.conversionRateOneUnit * +this.amount
			: this.totalReceived;
	}

	// Updates totals when the user changes the amount (input / max)
	private updateTotalFromAmountChange(): void {
		if (+this.amount > 0) {
			this.estimateLoading = true;
		} else {
			this.totalReceived = 0;
			this.totalReceivedEquivalent = 0;
		}
	}

	private updateTotalFromGraphql(offerFromGql: SwapOfferCreateMutation): void {
		const offer = offerFromGql.swapOfferCreate.swapOffer;
		this.totalReceived = +offer.targetTrade.amountAsset.value;
		this.totalReceivedEquivalent = +offer.sourceTrade.amountAsset.value;
		this.cdRef.markForCheck();
	}

	// Updates totals when the user: focusedOut the input, switched AgreeTerms, switched currencies
	private updateTotalFromAPIOfferResponse(): void {
		if (this.swapOfferAPIdata) {
			this.totalReceived = +this.swapOfferAPIdata.target_crypto_amount;

			this.totalReceivedEquivalent = +this.swapOfferAPIdata.source_crypto_amount;
		}
		this.cdRef.markForCheck();
	}

	public setMaxAmount(): void {
		this.amount = this.getMaxAmount();
	}

	// This set amount to maximum allowed (in case the user has typed a bigger amount than Max)
	public checkAmountBiggerThanMax(): void {
		const maxPermitedAmount = +this.getMaxAmount();
		if (+this.amount > maxPermitedAmount) {
			this.amount = String(maxPermitedAmount);
		}
	}

	public checkAmountSmallerThanMin(): void {
		//TODO: there is a known bug when swapping to index
		const minPermitedAmount = +this.getMinAmount(false);
		if (+this.amount < minPermitedAmount) {
			this.amount = String(minPermitedAmount);
		}
	}

	// This set amount to maximum allowed (in case the user has typed a bigger amount than Max)
	public checkAmountIsMax(): boolean {
		if (this.defaultPricesAPIdata) {
			const maxPermitedAmount = +this.getMaxAmount();
			if (+this.amount === maxPermitedAmount) {
				return true;
			}
		}
		return false;
	}

	public getMaxAmount(): string {
		if (this.defaultPricesAPIdata) {
			if (this.isIndex(this.leftSelection.currentWallet)) {
				return this.switchedInputCurrencies
					? this.defaultPricesAPIdata.source.wallet.target_amount
					: this.defaultPricesAPIdata.source.wallet.fiat_amount;
			}

			if (this.switchedInputCurrencies) {
				return this.defaultPricesAPIdata.source.wallet.target_amount;
			}

			return this.defaultPricesAPIdata.source.wallet.source_amount;
		}
		return "";
	}

	public getMinAmount(formatted: boolean = true, addSymbol: boolean = false): string {
		let minAmount: string;
		if (!this.defaultPricesAPIdata) {
			return "";
		}

		if (this.isIndex(this.leftSelection.currentWallet)) {
			return this.switchedInputCurrencies
				? this.defaultPricesAPIdata.source.minimum.target_amount
				: this.defaultPricesAPIdata.source.minimum.fiat_amount;
		}

		if (this.switchedInputCurrencies) {
			minAmount = this.defaultPricesAPIdata.source.minimum.target_amount;
		} else {
			minAmount = this.defaultPricesAPIdata.source.minimum.source_amount;
		}
		return minAmount;
	}

	private updateFields(): void {
		this.setConversionRateOneUnit();
		this.updateSwitchedAmount();
	}

	public isConversionVisible(): boolean {
		return this.showConversionRate && !this.isIndexSelected();
	}

	public isIndex(wallet: Wallet): boolean {
		const currency: Tradable = this.getWalletTradable(wallet);
		return !!currency && currency instanceof Index;
	}

	public isDerivative(wallet: Wallet): boolean {
		const currency: Tradable = this.getWalletTradable(wallet);
		return !!currency && currency.isDerivative();
	}

	public isToken(wallet: Wallet): boolean {
		return this.getWalletTradable(wallet).isToken();
	}

	public isLeftDropdownIndex(): boolean {
		return this.isIndex(this.leftSelection.currentWallet);
	}

	public isRightDropdownIndex(): boolean {
		return this.isIndex(this.rightSelection.currentWallet);
	}

	public isIndexSelected(): boolean {
		return this.isLeftDropdownIndex() || this.isRightDropdownIndex();
	}

	public isSameCurrency(): boolean {
		const first = this.leftSelection.currentWallet;
		const second = this.rightSelection.currentWallet;
		if (!!first && !!second) {
			return first.getCurrencyId() === second.getCurrencyId();
		}
		return false;
	}

	public showBothCurrencySymbols(): boolean {
		return (
			this.leftSelection.currentWallet instanceof IndexWallet &&
			this.rightSelection.currentWallet instanceof IndexWallet
		);
	}

	public getPrice(): SwapDefaultPricesModel {
		return this.defaultPricesAPIdata;
	}

	public inputOnFocus(e: FocusEvent): void {
		this.focusChanged = true;
		this.amountChanged = false;
	}

	// We do a API call to get the offer update, after the user finishes input typing
	public inputOnFocusOut(e: FocusEvent): void {
		if (this.amountChanged) {
			this.trackSelect("amount_input");
		}
	}

	public inputModelChange(e): void {
		if (this.focusChanged) {
			this.inputChanged = true;
		}
		if (this.amount !== e) {
			this.amount = e;
		}

		this.amountChanged = true;
		if (+this.amount !== +this.getMinAmount()) {
			this.swapToggles?.disableSliders();
		}
	}

	private startGraphqlOfferCountdown(expiresAt: number, serverTime: number): void {
		this.time = expiresAt - serverTime;

		clearInterval(this.oldTimer);
		const timer = window.setInterval(() => {
			if (--this.time <= 0) {
				clearInterval(timer);
				clearInterval(this.oldTimer);
				this.getApiSwapOffer(); // Take new offer
				this.swapToggles?.disableSliders();
				this.dynamic_terms?.resetToggles();
				this.infoService.setInfo(this.translationService.translate("offer_expired_message_title"));
			}
		}, 1000);
		this.oldTimer = timer;
	}

	private startCountingTermsDeactivate(swapOffer: SwapOfferModel): void {
		// Timer used to deactivate Agree Terms switch
		this.time = swapOffer.getValidity();
		clearInterval(this.oldTimer);
		const timer = window.setInterval(() => {
			if (--this.time <= 0) {
				clearInterval(timer);
				clearInterval(this.oldTimer);
				this.getApiSwapOffer(); // Take new offer
				this.swapToggles.disableSliders();
			}
		}, 1000);
		this.oldTimer = timer;
	}

	public fetchInfoText(): string {
		// Text for 3 separate info-boxes
		if (this.isSameCurrency()) {
			return "same_asset_warning";
		}
		let info: string = "swap_asset_";
		info = info + (this.showErrorSection() ? "call_error" : "please_select");
		return info;
	}

	public ngOnDestroy(): void {
		clearInterval(this.oldTimer);
		this.sliderValueChanged.complete();

		this.rudderstackFlowState(this.success ? "successful" : "cancelled", "Swap");
	}

	public isLegacyButtonDisabled(): boolean {
		return (
			(this.swapToggles?.isButtonDisabled() ?? true) ||
			this.apiPending ||
			+this.amount < +this.getMinAmount(false) ||
			+this.amount > +this.getMaxAmount()
		);
	}

	// Both dropdowns have derivatives as the selected values
	public bothSelectedDerivatives(): boolean {
		if (!this.hasStockTerms()) {
			return false;
		}
		return (
			this.isDerivative(this.rightSelection.currentWallet) && this.isDerivative(this.leftSelection.currentWallet)
		);
	}

	public checkForStockTest(): void {
		this.checkStocksTest(this.rightSelection);
		this.checkStocksTest(this.leftSelection);
	}

	private checkStocksTest(selection: SwapSelect): void {
		if (!selection.currentWallet) {
			return;
		}
		const tradableId = selection.currentWallet.getCurrencyId();
		if (this.stockTestService.needsTest(tradableId)) {
			this.stockTestHelperService.startTest(null, true);
		}
	}

	public hasStockTerms(): boolean {
		if (!this.rightSelection.currentWallet || !this.leftSelection.currentWallet) {
			return false;
		}
		return (
			this.isDerivative(this.rightSelection.currentWallet) || this.isDerivative(this.leftSelection.currentWallet)
		);
	}

	private resetStockWarnings(): void {
		if (this.swapOfferAPIdata) {
			this.swapOfferAPIdata.setStockWarnings([]);
		}
	}

	public initTracking(): void {
		this.rudderstackFlowState("started", "Swap");

		this.rudderstackPage("Swap Screen", "/trade/swap");

		this.sliderValueChanged.pipe(debounceTime(1000)).subscribe(() => {
			//update offer
			this.getApiSwapOffer();

			this.rudderstackTrack("Swap Slider Used");
		});
	}

	/*
		BEST changes the price with each trade, so it might happen to the user to trade with small fluctuation. It was decided to show "~" for BEST swaps.
	 */
	public showApproximateOnTotal(): boolean {
		if (!this.leftSelection.currentWallet || !this.rightSelection.currentWallet) {
			return false;
		}
		if (+this.amount === 0) {
			return false;
		}
		return (
			this.leftSelection.currentWallet.getCurrencyId() === TOKEN_BEST_ID ||
			this.rightSelection.currentWallet.getCurrencyId() === TOKEN_BEST_ID
		);
	}

	public getCustomSearch(): Function {
		return (searchInput: string, item: BpSelectOption) => {
			const wallet: Wallet = item.value;

			// By wallet name
			if (containsString(wallet.getName(), searchInput)) {
				return true;
			}

			// By tradable symbol
			const tradable: Tradable = this.getTradableById(wallet.getCurrencyId());
			if (!tradable) {
				return false;
			}
			if (containsString(tradable.getSymbol(), searchInput)) {
				return true;
			}

			// By tradable name
			return containsString(tradable.getName(), searchInput);
		};
	}

	trackFeeTransparency(): void {
		const properties = {
			trade_type: TradeTypeTrackingProperty.swap,
			order_type: OrderTypeTrackingProperty.market_order
		};
		const name = TradeTrackingName.TradeSummaryFeeSectionTapped;
		const data = { name, properties };

		this.rudderstackService.track(data);
	}

	public get dynamicTermsInvalid(): boolean {
		return (
			(this.dynamic_terms ? this.dynamic_terms?.termsAndConditionsForm?.invalid : true) ||
			+this.amount < +this.getMinAmount(false) ||
			+this.amount > +this.getMaxAmount()
		);
	}

	public get isButtonDisabled(): boolean {
		if (this.termsConditionsFf) {
			return this.dynamicTermsInvalid;
		}
		return this.isLegacyButtonDisabled();
	}
}

export class SwapSelect {
	private _fullWalletsList: Wallet[];
	private _filteredWalletsList: Wallet[];
	private _currentWallet: Wallet;
	private _selectionName: string;
	private _loadedTypes: (typeof Wallet)[];
	private _bpSelectOptions: BpSelectOption<Wallet>[] = null;

	public static readonly TYPE_CRYPTO: typeof TradableWallet = TradableWallet;
	public static readonly TYPE_FIAT: typeof FiatWallet = FiatWallet;

	// Public static readonly TYPE_METALS: string = "metals";

	public constructor(name: string, loadedTypes: (typeof Wallet)[]) {
		this.selectionName = name;
		this.loadedTypes = loadedTypes;
	}

	/*
		Full wallets list without filters. We alter / filter the list in _filteredWalletsList
		This is useful for future swap / filter between all assets / currencies / metals
	*/
	public get fullWalletsList(): Wallet[] {
		return this._fullWalletsList;
	}

	public set fullWalletsList(value: Wallet[]) {
		this._fullWalletsList = value;
	}

	// Altered / filtered version updated according to user's actions / restrictions:

	public get filteredWalletsList(): Wallet[] {
		return this._filteredWalletsList;
	}

	public set filteredWalletsList(value: Wallet[]) {
		this._filteredWalletsList = value;
	}

	public get selectionName(): string {
		return this._selectionName;
	}

	public set selectionName(value: string) {
		this._selectionName = value;
	}

	private isSelectionName(s: string): boolean {
		return this.selectionName === s;
	}

	public get isLeftSelection(): boolean {
		return this.isSelectionName("left");
	}

	public get isRightSelection(): boolean {
		return this.isSelectionName("right");
	}

	public get loadedTypes(): (typeof Wallet)[] {
		return this._loadedTypes;
	}

	public set loadedTypes(value: (typeof Wallet)[]) {
		this._loadedTypes = value;
	}

	public get currentWallet(): Wallet {
		return this._currentWallet;
	}

	public set currentWallet(wallet: Wallet) {
		this._currentWallet = wallet;
	}

	// Keep wallets with funds
	public applyFilterNonEmpty(): void {
		this.filteredWalletsList = this.filteredWalletsList.filter((wallet) => wallet.getBalance() > 0);
	}

	// Keep only default wallets from each currency
	public applyFilterOnlyDefault(): void {
		this.filteredWalletsList = this.filteredWalletsList.filter((wallet) => {
			if (wallet instanceof TradableWallet) {
				return wallet.isDefault();
			}

			if (wallet instanceof FiatWallet) {
				return true;
			} // We only have 1 default FIAT for each currency
		});
	}

	// Remove the wallet already selected in other dropdown
	public applyFilterRemoveSameWallet(duplicatedWallet: Wallet): void {
		if (duplicatedWallet) {
			this.filteredWalletsList = this.filteredWalletsList.filter(
				(wallet) => wallet.getName() !== duplicatedWallet.getName()
			);
		}
	}

	// Re-init of the filtered wallets list
	public refreshFilteredWallets(): void {
		this.filteredWalletsList = this.fullWalletsList;
	}

	public get bpSelectOptions(): BpSelectOption<Wallet>[] {
		return this._bpSelectOptions;
	}

	public set bpSelectOptions(o: BpSelectOption<Wallet>[]) {
		this._bpSelectOptions = o;
	}
}
